import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup, IconButton, MenuItem,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { ColumnBoxFlexFullWidth, ColumnFlexBox, FlexBoxFullWidth } from '../../common/boxes';
import { ICustomerFullProfile } from '../../../interfaces/customer';
import { TEXTS } from '../../../core/texts';
import { ShadowContainer } from '../../common/ShadowContainer';
import React, { ChangeEvent, SyntheticEvent, useCallback, useContext, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../interfaces/errorResponse';
import { ValidationErrorState } from '../../../types/validationErrorState';
import { IAdminUpdateFreelancerProfile } from '../../../interfaces/adminUpdateFreelancerProfile';
import SelectCountryAndTown from '../../common/SelectCountryAndTown';
import { ButtonWithLoader } from '../../common/ButtonWithLoader';
import { Validator } from '../../../core/Validator';
import NAMES = Validator.NAMES;
import { FreelancerService } from '../../../services/freelancer';
import { FreelancerContext } from './FreelancerEntity';
import { Close, Edit } from '@mui/icons-material';
import { CUSTOMER_STATUS_MAP, CustomerStatus } from '../../../enums/customerStatus';
import { useNotification } from '../../../+state/useNotification';


const FreelancerPersonalInfo = () => {
  const theme = useTheme();
  const { user, setUser } = useContext(FreelancerContext);
  const { openSuccessToast, openErrorToast } = useNotification();
  const [editMode, setEditMode] = useState(false);
  const [state, setState] = useState<IAdminUpdateFreelancerProfile>({...getEditablePropsFromUser(user), id: user.id});
  const [validationErrors, setValidationErrors] = useState<ValidationErrorState>({});

  const mutation = useMutation({
    mutationFn: (update: IAdminUpdateFreelancerProfile) => FreelancerService.editProfile(user.id, update),
    onSuccess: async (data) => {
      openSuccessToast('Successfully updated');
      setUser(data);
      setEditMode(false);
      setState(getEditablePropsFromUser(data));
    },
    onError: (error: AxiosError<IErrorResponse>) => {
      try {
        openErrorToast(error.response?.data.message || error.message);
      } catch (err) {
        openErrorToast('General error');
        console.log(err);
      }
    }
  });

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setState(prev => ({ ...prev, [name]: value }));
  }, []);

  const toggleEditMode = useCallback(() => setEditMode(isToggled => {
    if (isToggled) {
      setState(getEditablePropsFromUser(user));
      setValidationErrors({});
    }
    return !isToggled;
  }), [user]);

  const handleCheckboxChange = useCallback((e: SyntheticEvent<Element, Event>) => {
    const { checked, name } = e.target as any;
    setState(prev => ({ ...prev, [name]: checked }));
  }, []);

  const handleSubmit = useCallback(() => {
    const validErrors = Validator.validate(state, NAMES.PROFILE);
    if (validErrors) {
      return setValidationErrors(validErrors);
    }
    mutation.mutate(state);
  }, [state, mutation]);

  return (
    <ShadowContainer>
      <FlexBoxFullWidth justifyContent={'space-between'} mb={3}>
        <Typography variant={'h3'} sx={{ mb: 3, textAlign: 'center' }}>
          {TEXTS.PERSONAL_INFORMATION}
        </Typography>
        <Box>
          <IconButton color={editMode ? 'error' : 'primary'} onClick={toggleEditMode}>
            {
              editMode
                ? <Close/>
                : <Edit/>
            }
          </IconButton>
        </Box>
      </FlexBoxFullWidth>

      <ColumnBoxFlexFullWidth position={'relative'}>
        <Box mr={{ mobile: 0, laptop: 4 }} mb={2}>
          <Avatar sx={{
            border: `1px solid ${theme.palette.primary.dark}`,
            width: { mobile: 60, tablet: 100 },
            height: { mobile: 60, tablet: 100 },
            fontSize: { mobile: 22, tablet: 40 },
            mr: { mobile: 0, tablet: 4 }
          }}>
            {user.freelancerProfile.firstName[0] + user.freelancerProfile.lastName[0]}
          </Avatar>
        </Box>

        <ColumnFlexBox justifyContent={'center'} width={'100%'} maxWidth={500}>
          <TextField name={'id'} label={TEXTS.ID} variant={'standard'}
                     value={state.id} disabled={true} sx={{ mt: 2 }}
          />
          <TextField name={'firstName'} label={TEXTS.FIRST_NAME} variant={editMode ? 'outlined' : 'standard'}
                     error={!!validationErrors.firstName}
                     helperText={validationErrors.firstName} value={state.firstName} disabled={!editMode} sx={{ mt: 2 }}
                     onChange={handleInputChange}
          />
          <TextField name={'lastName'} label={TEXTS.LAST_NAME} variant={editMode ? 'outlined' : 'standard'}
                     error={!!validationErrors.lastName}
                     helperText={validationErrors.lastName} value={state.lastName} disabled={!editMode} sx={{ mt: 2 }}
                     onChange={handleInputChange}
          />
          <TextField name={'email'} label={TEXTS.EMAIL} variant={editMode ? 'outlined' : 'standard'}
                     error={!!validationErrors.email}
                     helperText={validationErrors.email} value={state.email} disabled={!editMode} sx={{ mt: 2 }}
                     onChange={handleInputChange}
          />
          <TextField name={'phone'} label={TEXTS.PHONE} variant={editMode ? 'outlined' : 'standard'}
                     error={!!validationErrors.phone}
                     helperText={validationErrors.phone} value={state.phone} disabled={!editMode} sx={{ mt: 2 }}
                     onChange={handleInputChange}
          />
          <SelectCountryAndTown setState={setState} countryValue={state.country!} townValue={state.town!}
                                validationErrors={validationErrors} disabled={!editMode}
          />
          <TextField name={'status'} label={TEXTS.STATUS} value={state.status} disabled={!editMode} sx={{ mt: 2 }}
                     error={!!validationErrors.status} helperText={validationErrors.status} onChange={handleInputChange}
                     select variant={editMode ? 'outlined' : 'standard'}
          >
            {
              Object.values(CustomerStatus).map(status => (
                <MenuItem key={status} value={status}>{CUSTOMER_STATUS_MAP[status]}</MenuItem>
              ))
            }
          </TextField>
          <TextField name={'summary'} label={'Summary'} variant={editMode ? 'outlined' : 'standard'}
                     error={!!validationErrors.summary}
                     helperText={validationErrors.summary} value={state.summary} disabled={!editMode} sx={{ mt: 2 }}
                     onChange={handleInputChange}
          />

          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel control={<Checkbox checked={state.emailVerified}/>} disabled={!editMode}
                              name={'emailVerified'} label={'Email Verified'} onChange={handleCheckboxChange}/>
            <FormControlLabel control={<Checkbox checked={state.rebenefitEnabled}/>} disabled={!editMode}
                              name={'rebenefitEnabled'} label={'re:Benefit Enabled'} onChange={handleCheckboxChange}/>
            <FormControlLabel control={<Checkbox checked={state.rebenefitClaimed}/>} disabled={!editMode}
                              name={'rebenefitClaimed'} label={'re:Benefit Claimed'} onChange={handleCheckboxChange}/>
          </FormGroup>
        </ColumnFlexBox>

      </ColumnBoxFlexFullWidth>

      {
        editMode &&
        <FlexBoxFullWidth justifyContent={'space-between'} mt={2}>
          <Button color={'error'} variant={'outlined'} onClick={toggleEditMode}>{TEXTS.CANCEL}</Button>
          <ButtonWithLoader variant={'outlined'} onClick={handleSubmit}>{'Save'}</ButtonWithLoader>
        </FlexBoxFullWidth>

      }
    </ShadowContainer>
  );
};

export default FreelancerPersonalInfo;

export function getEditablePropsFromUser(user: ICustomerFullProfile): IAdminUpdateFreelancerProfile {
  const {
    freelancerProfile: {
      firstName,
      lastName,
      country,
      town,
      summary,
      rebenefitClaimed,
      rebenefitEnabled,
    }, email, emailVerified, status, phone,
  } = user;
  return {
    firstName,
    lastName,
    country,
    town,
    summary,
    rebenefitClaimed,
    rebenefitEnabled,
    email,
    emailVerified,
    status,
    phone
  } as IAdminUpdateFreelancerProfile;
}